/**
 * ========================================
 * SALE CALCULATION SERVICE
 * Zentrale Berechnungslogik für alle Sale-Typen
 * ========================================
 */

import { getSaleLineUnitPrice } from '../domain/sales/saleLineMath';

class SaleCalculationService {
  constructor() {
    this.cache = new Map();
    this.cacheTimeout = 3 * 60 * 1000; // 3 Minuten Cache
  }

  /**
   * Berechne Sale-Totals (zentrale Logik)
   */
  calculateSaleTotals(saleProducts) {
    if (!saleProducts || !Array.isArray(saleProducts) || saleProducts.length === 0) {
      return {
        subtotal: 0,
        tax: 0,
        total: 0,
        itemCount: 0,
        totalQuantity: 0
      };
    }

    // Cache-Check
    const cacheKey = `sale_${saleProducts.length}_${saleProducts.map(p => `${p.id}_${p.quantity}_${p.total}`).join('_')}`;
    const cached = this.getFromCache(cacheKey);
    if (cached) return cached;

    // Berechne Subtotal
    const subtotal = saleProducts.reduce((sum, product) => {
      const total = Number(product.total || 0);
      return sum + total;
    }, 0);

    const tax = 0; // Steuer entfernt
    const total = subtotal + tax;
    const itemCount = saleProducts.length;
    const totalQuantity = saleProducts.reduce((sum, product) => sum + Number(product.quantity || 1), 0);

    const result = {
      subtotal: Math.round(subtotal * 100) / 100,
      tax: Math.round(tax * 100) / 100,
      total: Math.round(total * 100) / 100,
      itemCount,
      totalQuantity
    };

    // Cache-Ergebnis
    this.setCache(cacheKey, result);
    return result;
  }

  /**
   * Berechne Wechselgeld für Barzahlung
   */
  calculateChange(saleTotal, cashAmount) {
    const total = Number(saleTotal || 0);
    const cash = Number(cashAmount || 0);
    return Math.max(0, Math.round((cash - total) * 100) / 100);
  }

  /**
   * Validiere Barzahlung
   */
  validateCashPayment(saleTotal, cashAmount) {
    const total = Number(saleTotal || 0);
    const cash = Number(cashAmount || 0);
    
    return {
      isValid: cash >= total,
      change: this.calculateChange(total, cash),
      shortfall: cash < total ? Math.round((total - cash) * 100) / 100 : 0
    };
  }

  /**
   * Berechne Profit-Analyse für Sale
   * ADR-0013: domain/finance/profit (FIFO)
   */
  calculateProfitAnalysis(saleProducts, lots = [], purchases = []) {
    if (!saleProducts || !Array.isArray(saleProducts)) {
      return {
        totalProfit: 0,
        totalCost: 0,
        profitMargin: 0,
        items: []
      };
    }

    const { calculateProfit } = require('../domain/finance/profit');
    const total = saleProducts.reduce(
      (sum, p) =>
        sum + (Number(p.quantity) || 0) * getSaleLineUnitPrice(p),
      0
    );
    const syntheticSale = {
      products: saleProducts,
      total,
      totalAmount: total
    };
    const profit = calculateProfit({
      sales: [syntheticSale],
      purchases: purchases || [],
      expenses: [],
      lots
    });

    const items = saleProducts.map((product) => {
      const quantity = Number(product.quantity || 1);
      const sellingPrice = getSaleLineUnitPrice(product);
      let costTotal = 0;
      if (Array.isArray(product.usedLots) && product.usedLots.length > 0) {
        costTotal = product.usedLots.reduce(
          (sum, lot) =>
            sum +
            Number(lot.unitPrice || lot.purchaseUnitPrice || 0) *
              Number(lot.quantity || 0),
          0
        );
      } else if (Number(product.purchaseUnitPrice) > 0) {
        costTotal = Number(product.purchaseUnitPrice) * quantity;
      }
      const sellingTotal = sellingPrice * quantity;
      return {
        productId: product.productId || product.id,
        revenue: sellingTotal,
        cost: costTotal,
        profit: sellingTotal - costTotal
      };
    });

    return {
      totalProfit: profit.netProfit,
      totalCost: profit.purchaseCosts,
      profitMargin: profit.profitMargin,
      costSource: profit.costSource,
      items
    };
  }

  /**
   * Berechne Sale-Statistiken
   */
  calculateSaleStats(saleProducts) {
    if (!saleProducts || !Array.isArray(saleProducts)) {
      return {
        itemCount: 0,
        totalQuantity: 0,
        averageItemPrice: 0,
        mostExpensiveItem: null,
        cheapestItem: null,
        totalValue: 0
      };
    }

    const itemCount = saleProducts.length;
    const totalQuantity = saleProducts.reduce((sum, product) => sum + Number(product.quantity || 1), 0);
    const totalValue = saleProducts.reduce((sum, product) => sum + Number(product.total || 0), 0);
    const averageItemPrice = itemCount > 0 ? Math.round((totalValue / itemCount) * 100) / 100 : 0;

    let mostExpensiveItem = null;
    let cheapestItem = null;
    let maxPrice = 0;
    let minPrice = Infinity;

    saleProducts.forEach(product => {
      const itemTotal = Number(product.total || 0);
      if (itemTotal > maxPrice) {
        maxPrice = itemTotal;
        mostExpensiveItem = product;
      }
      if (itemTotal < minPrice) {
        minPrice = itemTotal;
        cheapestItem = product;
      }
    });

    return {
      itemCount,
      totalQuantity,
      averageItemPrice,
      mostExpensiveItem,
      cheapestItem,
      totalValue: Math.round(totalValue * 100) / 100
    };
  }

  /**
   * Berechne Sale-Discount
   */
  calculateDiscount(saleProducts, discountPercentage = 0, discountAmount = 0) {
    const totals = this.calculateSaleTotals(saleProducts);
    const subtotal = totals.subtotal;

    let discount = 0;
    
    if (discountPercentage > 0) {
      discount = Math.round((subtotal * discountPercentage / 100) * 100) / 100;
    } else if (discountAmount > 0) {
      discount = Math.round(discountAmount * 100) / 100;
    }

    const subtotalAfterDiscount = Math.max(0, subtotal - discount);
    const total = subtotalAfterDiscount + totals.tax;

    return {
      originalSubtotal: subtotal,
      discountPercentage,
      discountAmount: discount,
      subtotalAfterDiscount: Math.round(subtotalAfterDiscount * 100) / 100,
      total: Math.round(total * 100) / 100
    };
  }

  /**
   * Cache-Funktionen
   */
  getFromCache(key) {
    const cached = this.cache.get(key);
    if (cached && Date.now() - cached.timestamp < this.cacheTimeout) {
      return cached.data;
    }
    this.cache.delete(key);
    return null;
  }

  setCache(key, data) {
    this.cache.set(key, {
      data,
      timestamp: Date.now()
    });
  }

  clearCache() {
    this.cache.clear();
  }

  /**
   * Cache-Statistiken
   */
  getCacheStats() {
    return {
      size: this.cache.size,
      timeout: this.cacheTimeout
    };
  }
}

// Singleton-Instanz
const saleCalculationService = new SaleCalculationService();

export default saleCalculationService;

